import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonIcon from "@mui/icons-material/Person";
import BadgeIcon from "@mui/icons-material/Badge";
import EventIcon from "@mui/icons-material/Event";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import SchoolIcon from "@mui/icons-material/School";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const formatTimestamp = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") {
    return value.toDate().toLocaleString();
  }
  if (typeof value._seconds === "number") {
    return new Date(value._seconds * 1000).toLocaleString();
  }
  if (typeof value.seconds === "number") {
    return new Date(value.seconds * 1000).toLocaleString();
  }
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleString();
  }
  return null;
};

export default function USFSSyncStatus({
  linked,
  status,
  verifiedCount = 0,
  loading = false,
  onLink,
  onSync,
  onUnlink,
  syncing = false,
  unlinking = false,
  readOnly = false,
  credentialsStale = false,
}) {
  if (loading) {
    return (
      <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Your USFS Account
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Loading USFS status...
        </Typography>
      </Paper>
    );
  }

  const lastSync = formatTimestamp(status?.lastSync);
  const syncStatus = status?.syncStatus || "unknown";
  const isExpired = syncStatus === "expired";
  const memberNumber = status?.usfsUsername || status?.usfsPersonId || "";
  const profile = status?.profile || null;
  const hasStaleCredentials = credentialsStale || status?.credentialsStale;

  return (
    <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Your USFS Account
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Coaches, club admins, interclub admins, and skaters can link their personal U.S. Figure Skating membership to sync their profile and access protected member data.
      </Typography>

      {/* Linked State */}
      {linked && !isExpired && (
        <Box
          sx={{
            bgcolor: "success.50",
            border: "1px solid",
            borderColor: "success.main",
            borderRadius: 2,
            p: 2,
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <CheckCircleIcon color="success" />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                USFS Account Linked!
              </Typography>
              {memberNumber && (
                <Typography variant="body2" color="text.secondary">
                  Member Number: {memberNumber}
                </Typography>
              )}
            </Box>
          </Box>
          <Chip label="VERIFIED" color="success" size="small" />
        </Box>
      )}

      {/* Expired State */}
      {linked && isExpired && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Your USFS session has expired. Relink your account to resume syncing.
          {status?.syncError && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {status.syncError}
            </Typography>
          )}
        </Alert>
      )}

      {/* Credentials Stale - Password Changed */}
      {linked && !isExpired && credentialsStale && (
        <Alert 
          severity="warning" 
          icon={<WarningAmberIcon />}
          sx={{ mb: 2 }}
          action={
            <Button color="warning" size="small" onClick={onLink}>
              Update Credentials
            </Button>
          }
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            USFS Password Update Needed
          </Typography>
          <Typography variant="body2">
            Your USFS password may have changed. Please update your credentials to continue syncing your coach compliance and membership data.
          </Typography>
        </Alert>
      )}

      {/* Unlinked State */}
      {!linked && (
        <Box
          sx={{
            bgcolor: "info.50",
            border: "1px solid",
            borderColor: "info.main",
            borderRadius: 2,
            p: 2,
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <InfoOutlinedIcon color="info" sx={{ mt: 0.25 }} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Link Your USFS Account
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Securely connect your personal USFS membership to sync your profile, access test history, 
                club information, and protected member data. Coaches will also see their student rosters.
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Security Note */}
      {!linked && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: "center" }}>
          Your credentials are protected with bank-level, industry-standard encryption and are never shared.
        </Typography>
      )}

      {/* Profile Details (for linked state with profile data) */}
      {linked && !isExpired && profile && (() => {
        const hasCoachDetails = profile.coachCredentials?.isCoach;
        const cardSx = { p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" };

        const membershipCard = (
          <Paper elevation={0} sx={cardSx}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
              Your Membership Details
            </Typography>
            <Grid container spacing={2}>
              {profile.fullName && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Name</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{profile.fullName}</Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              {(profile.memberId || memberNumber) && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <BadgeIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Member Number</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: "monospace" }}>
                        {profile.memberId || memberNumber}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              {(profile.isCurrentMember !== null || profile.isCurrentLTSMember !== null) && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <VerifiedUserIcon 
                      fontSize="small" 
                      sx={{ 
                        color: (profile.isCurrentMember || profile.isCurrentLTSMember) 
                          ? "success.main" 
                          : "error.main" 
                      }} 
                    />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Membership Status</Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600,
                          color: (profile.isCurrentMember || profile.isCurrentLTSMember) 
                            ? "success.main" 
                            : "error.main"
                        }}
                      >
                        {profile.isCurrentMember 
                          ? "Active Member" 
                          : profile.isCurrentLTSMember 
                            ? "LTS Member" 
                            : "Inactive/Expired"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              {(profile.membershipExpiration || profile.ltsEndDate) && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <EventIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {profile.isCurrentMember ? "Membership Expires" : "LTS Valid Through"}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {profile.membershipExpiration || profile.ltsEndDate}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              {profile.eligibilityDescription && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircleIcon 
                      fontSize="small" 
                      sx={{ 
                        color: profile.eligibilityDescription?.toLowerCase().includes("eligible") 
                          ? "success.main" 
                          : "warning.main" 
                      }} 
                    />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Eligibility</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {profile.eligibilityDescription}
                        {profile.eligibilityStatus && ` (${profile.eligibilityStatus})`}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              {profile.club && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LocationOnIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Home Club</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{profile.club}</Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              {(profile.regionName || profile.sectionName) && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LocationOnIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Region / Section</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {[profile.regionName, profile.sectionName].filter(Boolean).join(" / ")}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        );

        const coachingCard = hasCoachDetails && (
          <Paper elevation={0} sx={cardSx}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
              Coach Credentials
            </Typography>
            <Grid container spacing={2}>
              {profile.coachCredentials.highestCertification && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <SchoolIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Certification</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {profile.coachCredentials.highestCertification}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              {profile.coachCredentials.status && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <VerifiedUserIcon 
                      fontSize="small" 
                      sx={{ 
                        color: profile.coachCredentials.isActive 
                          ? (profile.coachCredentials.isExpiring ? "warning.main" : "success.main")
                          : "error.main" 
                      }} 
                    />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Compliance Status</Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600,
                          color: profile.coachCredentials.isActive 
                            ? (profile.coachCredentials.isExpiring ? "warning.main" : "success.main")
                            : "error.main"
                        }}
                      >
                        {profile.coachCredentials.status}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              {profile.coachCredentials.complianceExpires && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <EventIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Compliance Expires</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {profile.coachCredentials.complianceExpires}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              {profile.coachCredentials.yearsExperience && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Experience</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {profile.coachCredentials.yearsExperience}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              {profile.coachCredentials.displayDisciplines && (
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <SchoolIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Disciplines</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {profile.coachCredentials.displayDisciplines}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              {profile.coachCredentials.rinks?.length > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <LocationOnIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Rinks</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {profile.coachCredentials.rinks.join(", ")}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              {profile.coachCredentials.isExpiring && (
                <Grid item xs={12}>
                  <Box 
                    sx={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: 1, 
                      bgcolor: "warning.50", 
                      p: 1.5, 
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "warning.200"
                    }}
                  >
                    <WarningAmberIcon fontSize="small" color="warning" />
                    <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 500 }}>
                      Your compliance is expiring soon. Please renew before {profile.coachCredentials.complianceExpires}.
                    </Typography>
                  </Box>
                </Grid>
              )}
              {!profile.coachCredentials.isActive && (
                <Grid item xs={12}>
                  <Box 
                    sx={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: 1, 
                      bgcolor: "error.50", 
                      p: 1.5, 
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "error.200"
                    }}
                  >
                    <WarningAmberIcon fontSize="small" color="error" />
                    <Typography variant="body2" color="error.dark" sx={{ fontWeight: 500 }}>
                      Your coach compliance has expired. Please renew your credentials through USFS.
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        );

        return (
          <Box sx={{ mb: 3 }}>
            <Divider sx={{ my: 2 }} />
            {hasCoachDetails ? (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>{membershipCard}</Grid>
                <Grid item xs={12} md={6}>{coachingCard}</Grid>
              </Grid>
            ) : (
              membershipCard
            )}
            <Divider sx={{ mt: 2 }} />
          </Box>
        );
      })()}

      {/* Last Verified (for linked state) */}
      {linked && !isExpired && lastSync && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Last verified: {lastSync}
        </Typography>
      )}

      {/* Verified Skaters Count (for linked state) */}
      {linked && !isExpired && verifiedCount > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {verifiedCount} skater{verifiedCount !== 1 ? "s" : ""} verified
        </Typography>
      )}

      {/* Action Buttons */}
      {!readOnly && (
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
          {!linked && (
            <Button
              variant="outlined"
              onClick={onLink}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 500,
                px: 3,
              }}
            >
              Link USFS Account
            </Button>
          )}

          {linked && isExpired && (
            <Button
              variant="contained"
              color="primary"
              onClick={onLink}
              disabled={syncing || unlinking}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 500,
                px: 3,
              }}
            >
              Relink Account
            </Button>
          )}

          {linked && !isExpired && (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={onSync}
                disabled={syncing}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 500,
                  px: 3,
                }}
              >
                {syncing ? "Refreshing..." : "Refresh Data"}
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={onUnlink}
                disabled={unlinking}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 500,
                  px: 3,
                }}
              >
                {unlinking ? "Unlinking..." : "Unlink Account"}
              </Button>
            </>
          )}

          {linked && isExpired && (
            <Button
              variant="outlined"
              color="error"
              onClick={onUnlink}
              disabled={unlinking}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 500,
                px: 3,
              }}
            >
              {unlinking ? "Unlinking..." : "Unlink Account"}
            </Button>
          )}
        </Box>
      )}

      {/* FAQ Section - Hidden until syncing features are complete */}
      {/* TODO: Re-enable FAQ when all USFS syncing features are implemented */}
    </Paper>
  );
}
